import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowLeft, ExternalLink, Share2, Tag } from 'lucide-react';
import DOMPurify from 'dompurify';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extract FAQ questions/answers from content_html for FAQPage schema
// Updated regex to handle HTML tags inside <dt> (e.g., <dt><strong>Question</strong></dt>)
const extractFAQFromContent = (contentHtml) => {
  if (!contentHtml) return [];
  
  const faqs = [];
  const dtDdRegex = /<dt[^>]*>([\s\S]*?)<\/dt>\s*<dd[^>]*>([\s\S]*?)<\/dd>/gi;
  let match;
  
  while ((match = dtDdRegex.exec(contentHtml)) !== null) {
    // Strip HTML tags from question and answer
    const question = match[1].replace(/<[^>]*>/g, '').trim();
    const answer = match[2].replace(/<[^>]*>/g, '').trim();
    if (question && answer) {
      faqs.push({ question, answer });
    }
  }
  
  return faqs;
};

// Generate Article schema
const generateArticleSchema = (article, slug) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description || article.excerpt || article.title,
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "author": {
      "@type": "Organization",
      "name": "Schweizer Nachfolge Experten AG",
      "url": "https://www.unternehmenbewerten.ch"
    },
    "publisher": {
      "@type": "Organization",
      "name": "unternehmenbewerten.ch",
      "url": "https://www.unternehmenbewerten.ch",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.unternehmenbewerten.ch/logo-full.webp"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.unternehmenbewerten.ch/blog/${slug}`
    },
    "image": article.image_url || "https://www.unternehmenbewerten.ch/og-image.webp"
  };
};

// Generate FAQPage schema
const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useSsgContent, setUseSsgContent] = useState(false);
  const ssgContentRef = useRef(null);

  useEffect(() => {
    // Check if SSG content was preserved by index.js before React mounted
    const ssgData = window.__SSG_BLOG__;
    
    if (ssgData && ssgData.detected && ssgData.slug === slug && ssgData.h1) {
      // SSG content is authoritative - use the preserved content
      setUseSsgContent(true);
      
      // Build article object from preserved SSG data to ensure H1 parity
      const ssgArticle = {
        title: ssgData.h1,
        content_html: ssgData.content || '',
        meta_description: document.querySelector('meta[name="description"]')?.getAttribute('content') || '',
        slug: slug,
        created_at: document.querySelector('meta[property="article:published_time"]')?.getAttribute('content') || new Date().toISOString(),
        author: document.querySelector('meta[property="article:author"]')?.getAttribute('content') || 'Schweizer Nachfolge Experten AG',
        category: 'Blog'
      };
      
      // Store the SSG content for rendering
      ssgContentRef.current = ssgData.content;
      
      setArticle(ssgArticle);
      setLoading(false);
      
      // Clear the global after use to avoid stale data on SPA navigation
      window.__SSG_BLOG__ = null;
      return;
    }

    // No SSG content - fetch from API (for client-side navigation within SPA)
    const fetchArticle = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/article/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Artikel nicht gefunden');
          } else {
            throw new Error('Fehler beim Laden');
          }
          return;
        }
        const data = await response.json();
        setArticle(data);
      } catch (err) {
        setError('Artikel konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  // Determine the content to render - prioritize SSG content
  // Remove H1 from content since we render it separately (to match SSG structure)
  let rawContent = useSsgContent && ssgContentRef.current 
    ? ssgContentRef.current 
    : (article?.content_html || '');
  
  // Remove any H1 tags from content to avoid duplicate H1s (H1 is rendered separately above)
  const contentToRender = rawContent.replace(/<h1[^>]*>.*?<\/h1>/gi, '').trim();

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="max-w-3xl mx-auto px-4 py-24 text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="max-w-3xl mx-auto px-4 py-24 text-center">
            <h1 className="text-2xl font-bold text-[#1a1c1b] mb-4">{error || 'Artikel nicht gefunden'}</h1>
            <Button onClick={() => navigate('/blog')} variant="outline">
              <ArrowLeft size={18} className="mr-2" />
              Zurück zum Blog
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Generate schemas from article data (only when NOT using SSG content - SSG already has schemas)
  const articleSchema = !useSsgContent ? generateArticleSchema(article, slug) : null;
  const faqs = !useSsgContent ? extractFAQFromContent(article.content_html) : [];
  const faqSchema = !useSsgContent ? generateFAQSchema(faqs) : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Only inject Helmet when NOT using SSG content (SSG already has all meta tags and schemas) */}
      {!useSsgContent && (
        <Helmet>
          <title>{`${article.title} – unternehmenbewerten.ch`}</title>
          <meta name="description" content={article.meta_description || article.excerpt || article.title} />
          <link rel="canonical" href={`https://www.unternehmenbewerten.ch/blog/${slug}`} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={article.meta_description || article.excerpt || article.title} />
          <meta property="og:url" content={`https://www.unternehmenbewerten.ch/blog/${slug}`} />
          {article.image_url && <meta property="og:image" content={article.image_url} />}
          <meta property="article:published_time" content={article.created_at} />
          <meta property="article:author" content={article.author || 'Schweizer Nachfolge Experten AG'} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={article.title} />
          <meta name="twitter:description" content={article.meta_description || article.excerpt || article.title} />
          {articleSchema && (
            <script type="application/ld+json">
              {JSON.stringify(articleSchema)}
            </script>
          )}
          {faqSchema && (
            <script type="application/ld+json">
              {JSON.stringify(faqSchema)}
            </script>
          )}
        </Helmet>
      )}
      
      <Header />
      
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="bg-[#f4f5f3] py-6">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-[#5a5d5b] hover:text-[#FF0000] transition-colors">Start</Link>
              <span className="text-[#888b89]">/</span>
              <Link to="/blog" className="text-[#5a5d5b] hover:text-[#FF0000] transition-colors">Blog</Link>
              <span className="text-[#888b89]">/</span>
              <span className="text-[#FF0000] truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Header */}
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          {/* Featured Image */}
          {article.image_url && !useSsgContent && (
            <div className="mb-8 rounded-2xl overflow-hidden shadow-lg bg-[#FFE5E5]" style={{ minHeight: '200px' }}>
              <img 
                src={article.image_url} 
                alt={article.title}
                className="w-full h-auto object-cover"
                onError={(e) => {
                  e.target.parentElement.style.display = 'none';
                }}
              />
            </div>
          )}
          
          {/* H1 - rendered separately to match SSG structure (SSG renders H1 outside content_html) */}
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1a1c1b] mb-6 leading-tight">
            {article.title}
          </h1>
          
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 rounded-full bg-[#FFE5E5] text-[#CC0000] text-sm font-medium flex items-center gap-1">
              <Tag size={14} />
              {article.category}
            </span>
            <div className="flex items-center gap-2 text-[#888b89] text-sm">
              <Calendar size={16} />
              <span>{new Date(article.created_at).toLocaleDateString('de-CH', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
            {article.word_count && (
              <span className="text-[#888b89] text-sm">
                {article.word_count} Wörter
              </span>
            )}
          </div>

          {/* Content - uses SSG content when available, otherwise API content */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:text-[#1a1c1b] prose-headings:font-bold
              prose-h1:text-3xl prose-h1:mb-6 prose-h1:mt-0
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:text-[#FF0000]
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-[#333] prose-p:leading-relaxed prose-p:text-justify prose-p:mb-4
              prose-a:text-[#FF0000] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-[#1a1c1b]
              prose-ul:my-4 prose-li:text-[#333]"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentToRender) }}
          />

          {/* Sources */}
          {article.sources && article.sources.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h2 className="text-xl font-bold text-[#1a1c1b] mb-6">Quellen & Referenzen</h2>
              <ul className="space-y-4">
                {article.sources.map((source) => (
                  <li key={source.url || source.title} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FFE5E5] text-[#FF0000] text-sm font-medium flex items-center justify-center">
                      {article.sources.indexOf(source) + 1}
                    </span>
                    <div>
                      <a 
                        href={source.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#FF0000] font-medium hover:underline flex items-center gap-1"
                      >
                        {source.title}
                        <ExternalLink size={14} />
                      </a>
                      {source.description && (
                        <p className="text-sm text-[#5a5d5b] mt-1">{source.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Keywords */}
          {article.keywords && article.keywords.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                {article.keywords.map((keyword) => (
                  <span 
                    key={keyword}
                    className="px-3 py-1 bg-gray-100 text-[#5a5d5b] text-sm rounded-full"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share & Back */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row gap-4 justify-between items-center">
            <Button 
              onClick={() => navigate('/blog')} 
              variant="outline"
              className="border-gray-300"
            >
              <ArrowLeft size={18} className="mr-2" />
              Zurück zum Blog
            </Button>
            
            <Button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: article.title,
                    url: window.location.href
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
              variant="outline"
              className="border-[#FF0000] text-[#FF0000] hover:bg-[#FF0000] hover:text-white"
            >
              <Share2 size={18} className="mr-2" />
              Artikel teilen
            </Button>
          </div>
        </article>

        {/* CTA */}
        <section className="py-16 bg-[#f4f5f3]">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">
              Ihr Unternehmen bewerten lassen?
            </h2>
            <p className="text-[#5a5d5b] mb-6">
              Erhalten Sie in wenigen Minuten eine indikative Bewertung Ihres Unternehmens.
            </p>
            <Button 
              onClick={() => navigate('/bewertung')}
              className="bg-[#FF0000] hover:bg-[#CC0000] text-white"
            >
              Jetzt kostenlos bewerten
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogArticlePage;
